import { useState, useEffect } from "react";
import { 
  Eye, 
  Type, 
  ZoomIn, 
  ZoomOut,
  Contrast, 
  RotateCcw, 
  X,
  MousePointer2,
  Link2,
  Minus,
  Plus,
  BookOpen,
  PauseCircle,
  Highlighter,
  ScanLine
} from "lucide-react";
import { cn } from "@/lib/utils";

interface AccessibilitySettings {
  fontSize: number; // 0 = default, -2 to +4 range
  highContrast: boolean;
  invertColors: boolean;
  grayscale: boolean;
  underlineLinks: boolean;
  bigCursor: boolean;
  readingGuide: boolean;
  textSpacing: number; // 0 = default, 0-3 range
  dyslexiaFont: boolean;
  highlightFocus: boolean;
  reducedMotion: boolean;
  hideImages: boolean;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 0,
  highContrast: false,
  invertColors: false,
  grayscale: false,
  underlineLinks: false,
  bigCursor: false,
  readingGuide: false,
  textSpacing: 0,
  dyslexiaFont: false,
  highlightFocus: false,
  reducedMotion: false,
  hideImages: false,
};

export function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    if (typeof window === 'undefined') return defaultSettings;
    const saved = localStorage.getItem("a11y-settings");
    return saved ? JSON.parse(saved) : defaultSettings;
  });
  const [readingGuideY, setReadingGuideY] = useState(0);

  // Apply settings on mount and changes
  useEffect(() => {
    applySettings(settings);
    localStorage.setItem("a11y-settings", JSON.stringify(settings));
  }, [settings]);

  // Reading guide mouse tracking
  useEffect(() => {
    if (settings.readingGuide) {
      const handleMouseMove = (e: MouseEvent) => {
        setReadingGuideY(e.clientY);
      };
      window.addEventListener("mousemove", handleMouseMove);
      return () => window.removeEventListener("mousemove", handleMouseMove);
    }
  }, [settings.readingGuide]);

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const applySettings = (s: AccessibilitySettings) => {
    const root = document.documentElement;

    // Font size
    const fontSizeMap: Record<number, string> = {
      [-2]: "85%",
      [-1]: "92%",
      0: "100%",
      1: "110%",
      2: "120%",
      3: "135%",
      4: "150%",
    };
    root.style.fontSize = fontSizeMap[s.fontSize] || "100%";

    // High contrast
    root.classList.toggle("a11y-high-contrast", s.highContrast);

    // Invert colors
    root.classList.toggle("a11y-invert", s.invertColors);

    // Grayscale
    root.classList.toggle("a11y-grayscale", s.grayscale);

    // Underline links
    root.classList.toggle("a11y-underline-links", s.underlineLinks);

    // Big cursor
    root.classList.toggle("a11y-big-cursor", s.bigCursor);

    // Text spacing
    root.classList.remove("a11y-spacing-1", "a11y-spacing-2", "a11y-spacing-3");
    if (s.textSpacing > 0) {
      root.classList.add(`a11y-spacing-${s.textSpacing}`);
    }

    // Dyslexia font
    root.classList.toggle("a11y-dyslexia", s.dyslexiaFont);

    // Highlight focus
    root.classList.toggle("a11y-focus-highlight", s.highlightFocus);

    // Reduced motion
    root.classList.toggle("a11y-reduced-motion", s.reducedMotion);

    // Hide images
    root.classList.toggle("a11y-hide-images", s.hideImages);
  };

  const hasActiveSettings = Object.entries(settings).some(([key, value]) => {
    if (key === "fontSize" || key === "textSpacing") return value !== 0;
    return value === true;
  });

  return (
    <>
      {/* Reading guide overlay */}
      {settings.readingGuide && (
        <div
          className="fixed left-0 right-0 h-12 pointer-events-none z-[9998] border-y-2 border-secondary/50 bg-secondary/10"
          style={{ top: readingGuideY - 24 }}
        />
      )}

      <div className="fixed bottom-6 left-6 z-[9999]">
        {/* Toggle button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300",
            "bg-secondary text-secondary-foreground hover:bg-secondary/90",
            hasActiveSettings && "ring-2 ring-secondary ring-offset-2 ring-offset-background"
          )}
          aria-label="Accessibility options"
          aria-expanded={isOpen}
        >
          <Eye className="h-6 w-6" />
        </button>

        {/* Panel */}
        {isOpen && (
          <div className="absolute bottom-16 left-0 w-80 max-h-[80vh] bg-card border border-border rounded-xl shadow-xl animate-scale-in overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-muted/50 sticky top-0">
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-secondary" />
                <span className="font-semibold text-foreground">Accessibility</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={resetSettings}
                  className="p-2 hover:bg-muted rounded-md transition-colors text-muted-foreground hover:text-foreground"
                  aria-label="Reset all settings"
                  title="Reset all"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                  aria-label="Close accessibility panel"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto max-h-[calc(80vh-60px)] p-3 space-y-4">
              {/* Font Size Control */}
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Type className="h-4 w-4 text-secondary" />
                    <span className="text-sm font-medium">Font Size</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {settings.fontSize > 0 ? `+${settings.fontSize}` : settings.fontSize}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateSetting("fontSize", Math.max(-2, settings.fontSize - 1))}
                    className="p-2 bg-muted hover:bg-muted/80 rounded-md transition-colors"
                    disabled={settings.fontSize <= -2}
                    aria-label="Decrease font size"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-secondary transition-all"
                      style={{ width: `${((settings.fontSize + 2) / 6) * 100}%` }}
                    />
                  </div>
                  <button
                    onClick={() => updateSetting("fontSize", Math.min(4, settings.fontSize + 1))}
                    className="p-2 bg-muted hover:bg-muted/80 rounded-md transition-colors"
                    disabled={settings.fontSize >= 4}
                    aria-label="Increase font size"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Text Spacing Control */}
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ZoomIn className="h-4 w-4 text-secondary" />
                    <span className="text-sm font-medium">Text Spacing</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {settings.textSpacing === 0 ? "Default" : `Level ${settings.textSpacing}`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateSetting("textSpacing", Math.max(0, settings.textSpacing - 1))}
                    className="p-2 bg-muted hover:bg-muted/80 rounded-md transition-colors"
                    disabled={settings.textSpacing <= 0}
                    aria-label="Decrease text spacing"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </button>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-secondary transition-all"
                      style={{ width: `${(settings.textSpacing / 3) * 100}%` }}
                    />
                  </div>
                  <button
                    onClick={() => updateSetting("textSpacing", Math.min(3, settings.textSpacing + 1))}
                    className="p-2 bg-muted hover:bg-muted/80 rounded-md transition-colors"
                    disabled={settings.textSpacing >= 3}
                    aria-label="Increase text spacing"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Color & Vision Section */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
                  Color & Vision
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <ToggleOption
                    icon={Contrast}
                    label="High Contrast"
                    active={settings.highContrast}
                    onClick={() => updateSetting("highContrast", !settings.highContrast)}
                  />
                  <ToggleOption
                    icon={Contrast}
                    label="Invert Colors"
                    active={settings.invertColors}
                    onClick={() => updateSetting("invertColors", !settings.invertColors)}
                  />
                  <ToggleOption
                    icon={Eye}
                    label="Grayscale"
                    active={settings.grayscale}
                    onClick={() => updateSetting("grayscale", !settings.grayscale)}
                  />
                  <ToggleOption
                    icon={ZoomOut}
                    label="Hide Images"
                    active={settings.hideImages}
                    onClick={() => updateSetting("hideImages", !settings.hideImages)}
                  />
                </div>
              </div>

              {/* Navigation Section */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
                  Navigation
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <ToggleOption
                    icon={Link2}
                    label="Underline Links"
                    active={settings.underlineLinks}
                    onClick={() => updateSetting("underlineLinks", !settings.underlineLinks)}
                  />
                  <ToggleOption
                    icon={MousePointer2}
                    label="Big Cursor"
                    active={settings.bigCursor}
                    onClick={() => updateSetting("bigCursor", !settings.bigCursor)}
                  />
                  <ToggleOption
                    icon={Highlighter}
                    label="Focus Highlight"
                    active={settings.highlightFocus}
                    onClick={() => updateSetting("highlightFocus", !settings.highlightFocus)}
                  />
                  <ToggleOption
                    icon={ScanLine}
                    label="Reading Guide"
                    active={settings.readingGuide}
                    onClick={() => updateSetting("readingGuide", !settings.readingGuide)}
                  />
                </div>
              </div>

              {/* Content Section */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
                  Content
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <ToggleOption
                    icon={BookOpen}
                    label="Dyslexia Font"
                    active={settings.dyslexiaFont}
                    onClick={() => updateSetting("dyslexiaFont", !settings.dyslexiaFont)}
                  />
                  <ToggleOption
                    icon={PauseCircle}
                    label="Stop Animations"
                    active={settings.reducedMotion}
                    onClick={() => updateSetting("reducedMotion", !settings.reducedMotion)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

interface ToggleOptionProps {
  icon: React.ElementType;
  label: string;
  active: boolean;
  onClick: () => void;
}

function ToggleOption({ icon: Icon, label, active, onClick }: ToggleOptionProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-center gap-2 p-3 rounded-lg transition-all text-center",
        "hover:bg-muted/50",
        active
          ? "bg-secondary/20 border border-secondary/40"
          : "bg-muted/20 border border-transparent"
      )}
      aria-pressed={active}
    >
      <div
        className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
          active ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground"
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <span className="text-xs font-medium text-foreground">{label}</span>
    </button>
  );
}
